/**
 * Service for managing company athletes directory
 */

import { buildApiUrl } from '../utils/apiConfig';
import { PageResponse } from '../types/pagination';
import { CompanyAthleteResponse, CompanyAthletesParams } from '../types/athletes';

/**
 * Fetches all athletes within the authenticated user's company
 * Requires STAFF or ADMINISTRATOR role
 *
 * @param params - Pagination and sorting parameters
 * @returns Promise<PageResponse<CompanyAthleteResponse>> - Paginated list of company athletes
 */
export const fetchCompanyAthletesApi = async (
  params: CompanyAthletesParams = {}
): Promise<PageResponse<CompanyAthleteResponse>> => {

  const queryParams = new URLSearchParams();

  if (params.page !== undefined) {
    queryParams.append('page', params.page.toString());
  }

  if (params.size !== undefined) {
    queryParams.append('size', params.size.toString());
  }

  if (params.sort) {
    queryParams.append('sort', params.sort);
  }

  if (params.query) {
    queryParams.append('query', params.query);
  }

  const queryString = queryParams.toString();
  const url = buildApiUrl(`api/athletes/company${queryString ? `?${queryString}` : ''}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include' // Send cookies automatically
  });

  if (response.status === 401) {
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (response.status === 403) {
    throw new Error('Access denied. Staff or Administrator role required.');
  }

  if (response.status === 500) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'User must be associated with a company');
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch company athletes: ${response.status}`);
  }

  return await response.json();
};

